import React, { useState, useRef } from 'react';
import './FileUpload.css';

/**
 * FileUpload Component
 * Provides drag-and-drop file upload with preview
 *
 * @param {Object} props - Component props
 * @param {Function} props.onFilesSelected - Callback when files are selected
 * @param {File[]} props.files - Currently selected files
 * @param {Function} props.onRemoveFile - Callback to remove a file
 * @param {boolean} [props.multiple] - Allow multiple file selection
 * @param {string} [props.accept] - Accepted file types
 * @param {number} [props.maxSize] - Max file size in bytes (default: 10MB)
 */
const FileUpload = ({
  onFilesSelected,
  files = [],
  onRemoveFile,
  multiple = true,
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB default
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Validate files
  const validateFiles = (fileList) => {
    const validFiles = [];
    let hasError = false;

    Array.from(fileList).forEach(file => {
      if (file.size > maxSize) {
        setError(`File "${file.name}" exceeds maximum size of ${formatFileSize(maxSize)}`);
        hasError = true;
        return;
      }
      validFiles.push(file);
    });

    if (!hasError) {
      setError(null);
    }

    return validFiles;
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const validFiles = validateFiles(selectedFiles);
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    }
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const validFiles = validateFiles(droppedFiles);
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    }
  };

  // Handle click to open file dialog
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Handle remove file
  const handleRemoveFile = (index) => {
    onRemoveFile(index);
    setError(null);
  };

  // Get file icon based on type
  const getFileIcon = (file) => {
    const type = file.type.toLowerCase();
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('zip') || type.includes('rar') || type.includes('compressed')) return '📦';
    return '📎';
  };

  return (
    <div className="file-upload-container">
      {/* Drop zone */}
      <div
        className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div className="drop-zone-content">
          <span className="upload-icon">📁</span>
          <p className="drop-zone-text">
            {isDragging ? (
              <strong>Drop files here</strong>
            ) : (
              <>
                <strong>Click to upload</strong> or drag and drop
              </>
            )}
          </p>
          <p className="drop-zone-hint">
            Max size: {formatFileSize(maxSize)}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="upload-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Selected files list */}
      {files.length > 0 && (
        <div className="selected-files-list">
          <h4>Selected Files ({files.length})</h4>
          <ul>
            {files.map((file, index) => (
              <li key={index} className="file-item">
                <span className="file-icon">{getFileIcon(file)}</span>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => handleRemoveFile(index)}
                  title="Remove file"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
